import { useState, useMemo, useEffect } from "react";
import { api, formatINR } from "../lib/api";
import AgentCard from "../components/AgentCard";
import { BadgeIndianRupee, TrendingUp, Calculator, ShieldCheck } from "lucide-react";

function calcEMI(principal, annualRate, years) {
    if (!principal || !years) return 0;
    const r = annualRate / 12 / 100;
    const n = years * 12;
    if (r === 0) return principal / n;
    return (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

export default function LoanServices() {
    const [amount, setAmount] = useState(5000000);
    const [rate, setRate] = useState(8.5);
    const [years, setYears] = useState(20);
    const [partners, setPartners] = useState([]);

    useEffect(() => {
        api.get("/partners", { params: { role: "loan_agent" } }).then((r) => setPartners(r.data || []));
    }, []);

    const emi = useMemo(() => calcEMI(amount, rate, years), [amount, rate, years]);
    const total = emi * years * 12;
    const interest = total - amount;

    return (
        <div className="min-h-screen bg-navy-900">
            <section className="bg-navy-950 border-b border-white/5">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
                    <div className="label-overline mb-3">Home Loans & Finance</div>
                    <h1 className="font-heading text-4xl lg:text-6xl font-bold text-white max-w-3xl">Best home-loan rates from <span className="text-gold-500">20+ banks.</span></h1>
                    <p className="text-white/65 mt-4 max-w-2xl">Calculate EMI, check eligibility, and connect with verified loan agents who negotiate the best deal on your behalf.</p>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
                {/* EMI CALC */}
                <div className="lg:col-span-7 rounded-3xl bg-navy-800 border border-gold-500/20 p-8 gold-border-glow" data-testid="emi-calculator">
                    <div className="flex items-center gap-2 mb-6">
                        <Calculator className="w-5 h-5 text-gold-500" />
                        <span className="label-overline">EMI Calculator</span>
                    </div>

                    <div className="space-y-6">
                        <Slider
                            label="Loan Amount"
                            value={amount}
                            min={500000}
                            max={50000000}
                            step={50000}
                            display={formatINR(amount)}
                            onChange={setAmount}
                            testid="loan-amount"
                        />
                        <Slider
                            label="Interest Rate (% p.a.)"
                            value={rate}
                            min={6}
                            max={15}
                            step={0.05}
                            display={`${rate.toFixed(2)}%`}
                            onChange={setRate}
                            testid="loan-rate"
                        />
                        <Slider
                            label="Loan Tenure"
                            value={years}
                            min={1}
                            max={30}
                            step={1}
                            display={`${years} yr`}
                            onChange={setYears}
                            testid="loan-tenure"
                        />
                    </div>

                    <div className="mt-8 grid grid-cols-3 gap-4">
                        <Stat label="Monthly EMI" value={formatINR(emi)} highlight />
                        <Stat label="Total Interest" value={formatINR(interest)} />
                        <Stat label="Total Payment" value={formatINR(total)} />
                    </div>
                </div>

                {/* SIDE PROMO */}
                <aside className="lg:col-span-5 space-y-5">
                    <div className="rounded-2xl bg-navy-800 border border-white/5 p-6">
                        <div className="flex items-center gap-2 mb-3 text-gold-500"><TrendingUp className="w-5 h-5" /><span className="label-overline">Today's Best Rates</span></div>
                        <table className="w-full text-sm">
                            <thead className="text-xs text-white/50 uppercase tracking-wider"><tr className="text-left"><th className="py-2">Bank</th><th>Rate (p.a.)</th><th className="text-right">Processing</th></tr></thead>
                            <tbody>
                                {[
                                    ["SBI", "8.40%", "0.35%"],
                                    ["HDFC", "8.50%", "0.50%"],
                                    ["ICICI", "8.55%", "0.50%"],
                                    ["Axis", "8.60%", "1%"],
                                    ["Bajaj Housing", "8.65%", "0.20%"],
                                ].map(([b, r, p]) => (
                                    <tr key={b} className="border-t border-white/5 text-white/80"><td className="py-2.5 font-medium">{b}</td><td className="text-gold-500 font-semibold">{r}</td><td className="text-right">{p}</td></tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="rounded-2xl bg-gold-500/10 border border-gold-500/30 p-6">
                        <ShieldCheck className="w-8 h-8 text-gold-500 mb-3" />
                        <h3 className="font-heading text-lg font-bold text-white mb-2">Why use our agents?</h3>
                        <ul className="text-sm text-white/75 space-y-1.5">
                            <li>• Negotiated rates not available publicly</li>
                            <li>• Document end-to-end handling</li>
                            <li>• 100% transparent — no hidden charges</li>
                            <li>• 7-day in-principle approval</li>
                        </ul>
                    </div>
                </aside>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24">
                <h2 className="font-heading text-3xl font-bold text-white mb-6">Verified loan specialists</h2>
                {partners.length === 0 ? (
                    <div className="text-center py-12 text-white/60 rounded-2xl bg-navy-800 border border-white/5">Loan agents launching shortly.</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
                        {partners.map((a) => <AgentCard key={a.id} agent={a} />)}
                    </div>
                )}
            </section>
        </div>
    );
}

function Slider({ label, value, min, max, step, display, onChange, testid }) {
    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-white/70">{label}</span>
                <span className="font-heading text-lg font-bold text-gold-500">{display}</span>
            </div>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                data-testid={testid}
                onChange={(e) => onChange(Number(e.target.value))}
                className="w-full accent-gold-500"
            />
        </div>
    );
}

function Stat({ label, value, highlight }) {
    return (
        <div className={`rounded-xl p-4 ${highlight ? "bg-gold-500 text-navy-900" : "bg-navy-900 border border-white/5 text-white"}`}>
            <div className={`text-xs uppercase tracking-wider ${highlight ? "text-navy-900/70" : "text-white/50"}`}>{label}</div>
            <div className="font-heading text-xl font-bold mt-1">{value}</div>
        </div>
    );
}
